import type { FormEvent } from 'react';
import clsx from 'clsx';
import React, {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { useHotkeys, useHotkeysContext } from 'react-hotkeys-hook';

import type { TWFontSizeProp } from '@fragment/ui/src/types/tailwind';

import { ResponsiveOffset } from '../ResponsiveOffset/ResponsiveOffset';

const ModalDepthContext = createContext(0);

const isTopmostModal = (element: HTMLElement | null) =>
  Array.from(document.querySelectorAll<HTMLElement>('[data-modal-depth]'))
    .sort(
      (leftModal, rightModal) =>
        Number(leftModal.dataset.modalDepth) -
        Number(rightModal.dataset.modalDepth)
    )
    .at(-1) === element;

const clickIsWithin = (element: HTMLElement, event: MouseEvent) => {
  const rect = element.getBoundingClientRect();
  return (
    event.clientX > rect.left &&
    event.clientX < rect.right &&
    event.clientY > rect.top &&
    event.clientY < rect.bottom
  );
};

// wrapping the entire modal in a form can cause unexpected behavior
// if the children are not intended to be inside a form
const ModalWrapper = ({
  onSubmit,
  children,
}: {
  onSubmit?: (e: FormEvent) => void;
  children: React.ReactNode;
}) => {
  const modalDepth = useContext(ModalDepthContext);
  return (
    <ModalDepthContext.Provider value={modalDepth + 1}>
      {onSubmit ? <form onSubmit={onSubmit}>{children}</form> : children}
    </ModalDepthContext.Provider>
  );
};

export type ModalWidth = 'xs' | 'sm' | 'md' | 'lg';

export type ModalProps<FontSizeProp> = {
  onClose: () => void;
  onModalWindowClose?: VoidFunction;
  onSubmit?: (e: FormEvent) => void;
  menu?: React.ReactNode;
  body: React.ReactNode;
  header?: React.ReactNode;
  backDropBlur?: boolean;
  footer?: React.ReactNode;
  bodyPadding?: boolean;
  'data-testid'?: string;
  fontSize?: TWFontSizeProp<FontSizeProp>;
  shouldCenter?: boolean;
  width?: ModalWidth;
  topBanner?: string;
  headerAboveMenu?: boolean;
  /** Override the top offset class passed to ResponsiveOffset. Defaults to "max-h-f12". */
  verticalOffset?: string;
};

export const Modal = <FontSizeProp,>({
  onClose,
  onSubmit,
  menu,
  header,
  footer,
  body,
  backDropBlur = true,
  bodyPadding = true,
  'data-testid': dataTestId,
  fontSize,
  shouldCenter = true,
  width = 'md',
  topBanner,
  headerAboveMenu = false,
  verticalOffset = 'max-h-f12',
}: ModalProps<FontSizeProp>) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const modalDepth = useContext(ModalDepthContext);

  useHotkeys(
    'esc',
    () => {
      if (isTopmostModal(modalRef.current)) onClose();
    },
    {
      enableOnFormTags: true,
      scopes: ['modal'],
    }
  );
  const { enableScope, disableScope } = useHotkeysContext();
  useEffect(() => {
    enableScope('modal');
    return () => {
      if (!document.querySelector('[data-modal-depth]')) {
        disableScope('modal');
      }
    };
  }, [disableScope, enableScope]);

  const modalContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isTopmostModal(modalRef.current) &&
        modalContentRef.current &&
        !modalContentRef.current.contains(event.target as Node) &&
        !clickIsWithin(modalContentRef.current, event)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const footerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (footerRef.current) {
      if (footerRef.current.childElementCount === 0) {
        // Collapse the footer if it has no children
        footerRef.current.parentElement?.setAttribute('style', 'display: none');
      } else {
        footerRef.current.parentElement?.setAttribute(
          'style',
          'display: block'
        );
      }
    }
    // Don't remove the dependency on footer. We need this useEffect to run when footer changes.
  }, [footerRef, footer]);

  return createPortal(
    <ModalWrapper onSubmit={onSubmit}>
      <div
        ref={modalRef}
        data-modal-depth={modalDepth}
        style={{ zIndex: 30 + modalDepth }}
        data-testid={dataTestId}
        className={clsx('relative z-30', fontSize)}
        aria-labelledby={header ? titleId : undefined}
        role="dialog"
        aria-modal="true"
      >
        <div
          data-testid="modal-bg"
          className={clsx(
            `fixed inset-0`,
            backDropBlur && 'bg-main opacity-[.15]'
          )}
        />
        <div className="fixed inset-0 z-10 w-screen overflow-y-scroll scrollbar-hide flex justify-center">
          <div className="relative top-0">
            <ResponsiveOffset
              verticalOffset={verticalOffset}
              shouldCenter={shouldCenter}
            >
              {topBanner && (
                <div className="bg-black h-[25px] text-white text-center flex items-center justify-center mx-f2">
                  <p className="font-medium">{topBanner}</p>
                </div>
              )}
              <div
                ref={modalContentRef}
                data-testid="modal-parent-div"
                className={clsx(
                  'flex flex-col',
                  'fragment-modal-content', // This is used to identify when a modal is open
                  'bg-canvas h-fit',
                  'w-[calc(100dvw-4ch)] tablet:w-[calc(100dvw-8ch)]',
                  width === 'xs' && 'laptop:w-[400px]',
                  width === 'sm' && 'laptop:w-[800px]',
                  width === 'md' &&
                    'laptop:w-[60dvw] largeLaptop:w-[66dvw] desktop:w-[50dvw]',
                  width === 'lg' &&
                    'laptop:w-[calc(100dvw-8ch)] desktop:w-[85dvw]',
                  'mx-f2 border-main-200'
                )}
              >
                {headerAboveMenu && header && (
                  <header
                    id={titleId}
                    className={clsx(
                      'w-full px-f2 py-f3 text-left h-f2 flex items-center justify-between border-b-[0.5px] border-main-300'
                    )}
                  >
                    {header}
                  </header>
                )}
                <div className="flex w-full h-full ">
                  {menu && (
                    <div className="border-r-[0.5px] border-main-300 p-f2 w-[20ch] shrink-0">
                      <div
                        className="flex flex-col min-h-full"
                        data-testid="modal-side-menu"
                      >
                        {menu}
                      </div>
                    </div>
                  )}
                  <div
                    className={clsx([
                      'flex flex-col items-center text-center max-h-full',
                      menu ? 'min-w-f0 flex-1' : 'w-full',
                      bodyPadding && 'p-f2',
                    ])}
                    data-testid="modal-content"
                  >
                    {!headerAboveMenu && header && (
                      <header
                        id={titleId}
                        className={clsx(
                          'w-full place-self-start text-left h-f2'
                        )}
                      >
                        {header}
                      </header>
                    )}

                    <main
                      className={clsx([
                        !headerAboveMenu && header ? 'mt-f4' : '',
                        footer ? 'pb-f2' : '',
                        'relative transform bg-canvas text-left transition-all w-full h-full max-h-full',
                      ])}
                    >
                      {body}
                    </main>
                  </div>
                </div>
                {footer && (
                  <>
                    <footer
                      className={clsx('w-full')}
                      data-testid="modal-footer"
                    >
                      <hr className="border-main-200 w-full" />
                      <div
                        ref={footerRef}
                        id="footer-container"
                        className="w-full flex space-y-f2 p-f2"
                      >
                        {footer}
                      </div>
                    </footer>
                  </>
                )}
              </div>
            </ResponsiveOffset>
          </div>
        </div>
      </div>
    </ModalWrapper>,
    document.body
  );
};
